import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Star } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { formatApiErrorDetail } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRow = ({ n, size = "h-4 w-4" }) => (
  <div className="flex gap-1" aria-label={`${n} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} ${i < n ? "fill-accentlight text-accentlight" : "text-white/20"}`} />
    ))}
  </div>
);

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data);
    } catch {
      setReviews([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/reviews`, { name: name.trim(), rating, text: text.trim() });
      setSent(true);
      setName("");
      setText("");
      setRating(5);
    } catch (error) {
      setErr(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
    setBusy(false);
  };

  return (
    <section id="reviews" className="relative py-24 md:py-36" data-testid="reviews-section">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <SectionHeading
          eyebrow="Star reviews"
          title={
            <>
              SHARE YOUR
              <br />
              <span className="bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent">
                GAME DAY
              </span>
            </>
          }
        />

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-white/5 bg-surface p-8" data-testid="review-form-card">
              <h3 className="font-display text-2xl tracking-wider">WRITE A REVIEW</h3>
              <p className="mt-2 text-sm text-white/50">
                Played with us? Tell other families what to expect.
              </p>

              {sent ? (
                <div
                  className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-4 text-sm text-emerald-200"
                  data-testid="review-success"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  Thank you! Your review was received — it appears here once it's approved.
                </div>
              ) : null}

              <form onSubmit={submit} data-testid="review-form" className="mt-6">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                  Your rating
                </label>
                <div className="flex gap-2" data-testid="review-star-picker">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      aria-label={`Rate ${n} out of 5 stars`}
                      data-testid={`review-star-${n}`}
                      className="transition-transform duration-150 hover:scale-110"
                    >
                      <Star
                        className={`h-8 w-8 transition-colors ${
                          n <= (hover || rating) ? "fill-accentlight text-accentlight" : "text-white/20"
                        }`}
                      />
                    </button>
                  ))}
                </div>

                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  maxLength={80}
                  autoComplete="name"
                  data-testid="review-name-input"
                  className="mt-6 w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
                />
                <textarea
                  rows={4}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Tell us about your game day — the photos, the service, anything other families should know…"
                  required
                  minLength={10}
                  maxLength={600}
                  data-testid="review-text-input"
                  className="mt-3 w-full resize-none rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
                />
                {err && (
                  <p className="mt-3 text-xs text-red-300" data-testid="review-error">
                    {err}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="review-submit-button"
                  className="mt-4 w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
                >
                  Send review
                </button>
                <p className="mt-3 text-[11px] text-white/30">
                  Reviews appear after a quick approval — keeps the spam out.
                </p>
              </form>
            </div>
          </Reveal>

          <div className="space-y-4">
            {reviews.length === 0 ? (
              <Reveal delay={0.25}>
                <div
                  className="flex h-full min-h-[240px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/10 p-10 text-center"
                  data-testid="reviews-empty"
                >
                  <Star className="h-8 w-8 text-white/20" strokeWidth={1.5} />
                  <p className="text-white/50">No reviews yet — be the first to share your game day.</p>
                </div>
              </Reveal>
            ) : (
              reviews.map((r, i) => (
                <Reveal key={r.id} delay={0.2 + i * 0.07}>
                  <figure
                    className="rounded-2xl border border-white/5 bg-surface/60 p-6"
                    data-testid={`review-card-${r.id}`}
                  >
                    <StarRow n={r.rating} />
                    <blockquote className="mt-4 text-sm leading-relaxed text-white/80">
                      &ldquo;{r.text}&rdquo;
                    </blockquote>
                    <figcaption className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-white/50">
                      {r.name}
                    </figcaption>
                  </figure>
                </Reveal>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}